import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import axios from "axios";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftSelect from "components/SoftSelect";
import SoftSnackbar from "components/SoftSnackbar";
import FlagLoader from "layouts/flagloader";
import { API_URL } from "config";
import { useAuth } from "../../AuthContext";

import AddIcon from "@mui/icons-material/Add";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import EditIcon from "@mui/icons-material/Edit";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Card from "@mui/material/Card";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";

const booleanOptions = [
  { value: "1", label: "Yes" },
  { value: "0", label: "No" },
];

const statusOptions = [
  { value: "Active", label: "Active" },
  { value: "Inactive", label: "Inactive" },
];

const companyCountryOptions = [
  { value: "IN", label: "IN" },
  { value: "UAE", label: "UAE" },
];

const emptyForm = {
  company_id: "",
  country: "",
  company_name: "",
  company_logo: null,
  existing_company_logo: "",
  address: "",
  city: "",
  email: "",
  website: "",
  phone: "",
  state: "",
  pincode: "",
  gstin: "",
  vat_number: "",
  lut_number: "",
  pan: "",
  msme_registration_no: "",
  hsn_code: "",
  additional_company_fields: [],
  bank_name: "",
  account_holder: "",
  account_number: "",
  ifsc_code: "",
  swift_code: "",
  additional_bank_fields: [],
  enable_tax: false,
  tax_percentage: "",
  status: "Active",
  enable_signature: false,
  signature_image: null,
  existing_signature_image: "",
  invoice_series_pattern: "QS/{year}-{month}/{series}",
  enable_invoice_description: false,
  invoice_description: "",
  created_at: "",
  updated_at: "",
};

const editableFields = [
  "country",
  "company_name",
  "company_logo",
  "address",
  "city",
  "email",
  "website",
  "phone",
  "state",
  "pincode",
  "gstin",
  "vat_number",
  "lut_number",
  "pan",
  "msme_registration_no",
  "hsn_code",
  "additional_company_fields",
  "bank_name",
  "account_holder",
  "account_number",
  "ifsc_code",
  "swift_code",
  "additional_bank_fields",
  "enable_tax",
  "tax_percentage",
  "status",
  "enable_signature",
  "signature_image",
  "invoice_series_pattern",
  "enable_invoice_description",
  "invoice_description",
];

const tableHeaderSx = {
  backgroundColor: "#f8f9fa",
  color: "#8392ab",
  fontSize: "12px",
  fontWeight: 500,
  borderBottom: "1px solid #e2e8f0",
  py: 1,
  px: 1,
  whiteSpace: "nowrap",
};

const tableBodySx = {
  color: "#344767",
  fontSize: "12px",
  borderBottom: "1px solid #edf2f7",
  py: 0.8,
  px: 1,
  verticalAlign: "middle",
};

const formInputSx = {
  "& .MuiInputBase-root": {
    minHeight: "38px",
    fontSize: "13px",
  },
};

const listFilterControlSx = {
  width: "100%",
  height: "40px",
  minHeight: "40px",
  "--soft-select-height": "40px",
  "& .MuiInputBase-root": {
    width: "100%",
    height: "40px",
    minHeight: "40px",
  },
};

// DashboardLayout globally compacts inputs whose placeholder contains "search".
// This page keeps its search control aligned with the adjacent Company select.
const companySearchControlSx = {
  ...listFilterControlSx,
  "&&": {
    width: "100% !important",
    maxWidth: "none !important",
    height: "40px !important",
  },
  "&& > .MuiInputBase-root": {
    width: "100% !important",
    maxWidth: "none !important",
    height: "40px !important",
    minHeight: "40px !important",
    padding: "8px 12px !important",
  },
  "&& > .MuiInputBase-root input": {
    width: "100% !important",
    height: "22px !important",
    fontSize: "0.875rem !important",
  },
};

const parseStoredPermissions = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem("permissions") || "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const formatDate = (value) => {
  if (!value) return "--";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "--";
  return date.toLocaleDateString("en-GB");
};

const normalizeAdditionalFields = (fields) => {
  if (!Array.isArray(fields)) return [];

  return fields.map((item) => ({
    name: String(item?.name ?? item?.field ?? item?.label ?? ""),
    value: String(item?.value ?? ""),
  }));
};

function CompanyFieldRow({
  label,
  field,
  form,
  canField,
  readOnly,
  onChange,
  multiline = false,
  required = false,
}) {
  if (!canField(field)) return null;

  return (
    <Grid container spacing={1} alignItems="center" sx={{ py: 0.75, borderTop: "1px solid #edf2f7" }}>
      <Grid item xs={12} md={4}>
        <SoftTypography variant="button" fontWeight="medium" color="text">
          {label}{required ? " *" : ""}
        </SoftTypography>
      </Grid>
      <Grid item xs={12} md={8}>
        <SoftBox sx={formInputSx}>
          <SoftInput
            value={form[field] ?? ""}
            onChange={(event) => onChange(field, event.target.value)}
            placeholder={field === "company_id" && !form[field] ? "Will be auto generated" : label}
            disabled={readOnly || field === "company_id"}
            multiline={multiline}
            rows={multiline ? 2 : undefined}
          />
        </SoftBox>
      </Grid>
    </Grid>
  );
}

CompanyFieldRow.propTypes = {
  label: PropTypes.string.isRequired,
  field: PropTypes.string.isRequired,
  form: PropTypes.object.isRequired,
  canField: PropTypes.func.isRequired,
  readOnly: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  multiline: PropTypes.bool,
  required: PropTypes.bool,
};

CompanyFieldRow.defaultProps = {
  multiline: false,
  required: false,
};

function FormSection({ title, children, showFieldHeader = false }) {
  return (
    <SoftBox mb={2}>
      <SoftTypography variant="h6" fontWeight="medium" mb={1}>{title}</SoftTypography>
      <SoftBox sx={{ border: "1px solid #e2e8f0", borderRadius: "8px", px: 1.5, pb: 0.5 }}>
        {showFieldHeader && (
          <Grid container spacing={1} sx={{ py: 1 }}>
            <Grid item xs={12} md={4}><SoftTypography variant="caption" fontWeight="bold">FIELD</SoftTypography></Grid>
            <Grid item xs={12} md={8}><SoftTypography variant="caption" fontWeight="bold">VALUE</SoftTypography></Grid>
          </Grid>
        )}
        {children}
      </SoftBox>
    </SoftBox>
  );
}

FormSection.propTypes = {
  title: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  showFieldHeader: PropTypes.bool,
};

FormSection.defaultProps = {
  showFieldHeader: false,
};

function AdditionalDetailFields({ title, fields, readOnly, canAdd, canRemove, onChange }) {
  const normalizedFields = normalizeAdditionalFields(fields);

  const updateRow = (index, key, value) => {
    onChange(normalizedFields.map((item, itemIndex) => (
      itemIndex === index ? { ...item, [key]: value } : item
    )));
  };

  return (
    <SoftBox sx={{ py: 1, borderTop: "1px solid #edf2f7" }}>
      <SoftTypography variant="button" fontWeight="bold" color="dark">
        {title}
      </SoftTypography>

      {normalizedFields.length === 0 ? (
        <SoftTypography variant="caption" color="text" display="block" mt={0.75}>
          No additional details added.
        </SoftTypography>
      ) : (
        <>
          <Grid container spacing={1} mt={0.25} mb={0.25}>
            <Grid item xs={12} md={canRemove && !readOnly ? 5 : 6}>
              <SoftTypography variant="caption" fontWeight="bold">FIELD NAME</SoftTypography>
            </Grid>
            <Grid item xs={12} md={canRemove && !readOnly ? 5 : 6}>
              <SoftTypography variant="caption" fontWeight="bold">VALUE</SoftTypography>
            </Grid>
          </Grid>
          {normalizedFields.map((item, index) => (
            <Grid container spacing={1} alignItems="center" mb={0.75} key={`${title}-${index}`}>
              <Grid item xs={12} md={canRemove && !readOnly ? 5 : 6}>
                <SoftBox sx={formInputSx}>
                  <SoftInput
                    value={item.name}
                    placeholder="Field Name"
                    disabled={readOnly}
                    onChange={(event) => updateRow(index, "name", event.target.value)}
                  />
                </SoftBox>
              </Grid>
              <Grid item xs={12} md={canRemove && !readOnly ? 5 : 6}>
                <SoftBox sx={formInputSx}>
                  <SoftInput
                    value={item.value}
                    placeholder="Value"
                    disabled={readOnly}
                    onChange={(event) => updateRow(index, "value", event.target.value)}
                  />
                </SoftBox>
              </Grid>
              {canRemove && !readOnly && (
                <Grid item xs={12} md={2}>
                  <IconButton
                    size="small"
                    color="error"
                    title="Remove detail"
                    onClick={() => onChange(normalizedFields.filter((_, itemIndex) => itemIndex !== index))}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Grid>
              )}
            </Grid>
          ))}
        </>
      )}

      {canAdd && !readOnly && (
        <SoftButton
          variant="outlined"
          color="info"
          size="small"
          sx={{ mt: 0.5 }}
          onClick={() => onChange([...normalizedFields, { name: "", value: "" }])}
        >
          <AddIcon sx={{ mr: 0.5 }} /> Add Additional Detail
        </SoftButton>
      )}
    </SoftBox>
  );
}

AdditionalDetailFields.propTypes = {
  title: PropTypes.string.isRequired,
  fields: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    field: PropTypes.string,
    label: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  })),
  readOnly: PropTypes.bool.isRequired,
  canAdd: PropTypes.bool.isRequired,
  canRemove: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
};

AdditionalDetailFields.defaultProps = {
  fields: [],
};

function Companies() {
  const token = localStorage.getItem("authToken");
  const { isAuthReady, permissions = [], userPermission = [] } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [filterOptions, setFilterOptions] = useState({ companies: [], statuses: statusOptions });
  const [clientStates, setClientStates] = useState([]);
  const [statesLoading, setStatesLoading] = useState(false);
  const [filters, setFilters] = useState({
    search: "",
    start_date: "",
    end_date: "",
    company_ids: [],
    statuses: [],
  });
  const [pagination, setPagination] = useState({ currentPage: 1, lastPage: 1, from: 0, to: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState("view");
  const [dialogLoading, setDialogLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [selectedRows, setSelectedRows] = useState([]);
  const [bulkStatus, setBulkStatus] = useState("Active");
  const [isExporting, setIsExporting] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuCompany, setMenuCompany] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", color: "success" });

  const signaturePreview = useMemo(() => {
    if (typeof File !== "undefined" && form.signature_image instanceof File) {
      return URL.createObjectURL(form.signature_image);
    }
    return form.existing_signature_image || "";
  }, [form.existing_signature_image, form.signature_image]);

  const logoPreview = useMemo(() => {
    if (typeof File !== "undefined" && form.company_logo instanceof File) {
      return URL.createObjectURL(form.company_logo);
    }
    return form.existing_company_logo || "";
  }, [form.company_logo, form.existing_company_logo]);

  useEffect(() => () => {
    if (signaturePreview.startsWith("blob:")) URL.revokeObjectURL(signaturePreview);
  }, [signaturePreview]);

  useEffect(() => () => {
    if (logoPreview.startsWith("blob:")) URL.revokeObjectURL(logoPreview);
  }, [logoPreview]);

  const grantedPermissions = useMemo(
    () => Array.from(new Set([...permissions, ...userPermission, ...parseStoredPermissions()])),
    [permissions, userPermission]
  );

  const hasPermission = useCallback((...requiredPermissions) => {
    if (grantedPermissions.includes("access.system.full_access")) return true;
    return requiredPermissions.flat().filter(Boolean).some((key) => grantedPermissions.includes(key));
  }, [grantedPermissions]);

  const canField = useCallback(
    (field) => hasPermission(`companies.fields.${field === "city" ? "address" : field}.view`),
    [hasPermission]
  );
  const canFilter = useCallback(
    (filter) => hasPermission(`companies.filters.${filter}.view`),
    [hasPermission]
  );

  const canAccess = hasPermission("companies.pages.list.view");
  const canCreate = hasPermission("companies.actions.create");
  const canView = hasPermission("companies.actions.view");
  const canEdit = hasPermission("companies.actions.edit");
  const canExport = hasPermission("companies.actions.export");
  const canBulkStatus = hasPermission("companies.actions.bulk_status") && canField("status");
  const canAddCompanyField = hasPermission("companies.actions.add_company_field");
  const canRemoveCompanyField = hasPermission("companies.actions.remove_company_field");
  const canAddBankField = hasPermission("companies.actions.add_bank_field");
  const canRemoveBankField = hasPermission("companies.actions.remove_bank_field");
  const showActions = canField("actions") && (canView || canEdit);

  const requestConfig = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  const showMessage = useCallback((message, color = "success") => {
    setSnackbar({ open: true, message, color });
  }, []);

  useEffect(() => {
    if (!dialogOpen || form.country !== "IN" || !canField("state")) {
      setClientStates([]);
      setStatesLoading(false);
      return undefined;
    }

    let active = true;
    setStatesLoading(true);
    axios
      .get(`${API_URL}/client-states?country_name=India`, requestConfig)
      .then((response) => {
        if (active) setClientStates(response.data?.data || []);
      })
      .catch((error) => {
        if (active) {
          setClientStates([]);
          showMessage(error.response?.data?.message || "Failed to load India states", "error");
        }
      })
      .finally(() => {
        if (active) setStatesLoading(false);
      });

    return () => {
      active = false;
    };
  }, [canField, dialogOpen, form.country, requestConfig, showMessage]);

  const appendFilters = useCallback((params) => {
    if (canFilter("search") && filters.search.trim()) params.set("q", filters.search.trim());
    if (canFilter("start_date") && filters.start_date) params.set("start_date", filters.start_date);
    if (canFilter("end_date") && filters.end_date) params.set("end_date", filters.end_date);
    if (canFilter("company")) {
      filters.company_ids.forEach((id) => params.append("company_ids[]", id));
    }
    if (canFilter("status")) {
      filters.statuses.forEach((status) => params.append("statuses[]", status));
    }
  }, [canFilter, filters]);

  const fetchCompanies = useCallback(async (page = 1) => {
    if (!canAccess) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const params = new URLSearchParams({ page: String(page), per_page: "20" });
      appendFilters(params);
      const response = await axios.get(`${API_URL}/companies?${params.toString()}`, requestConfig);
      const pageData = response.data?.companies || {};
      setCompanies(pageData.data || []);
      setPagination({
        currentPage: pageData.current_page || 1,
        lastPage: pageData.last_page || 1,
        from: pageData.from || 0,
        to: pageData.to || 0,
        total: pageData.total || 0,
      });
      setSelectedRows([]);
    } catch (error) {
      showMessage(error.response?.data?.message || "Failed to load companies", "error");
    } finally {
      setLoading(false);
    }
  }, [API_URL, appendFilters, canAccess, requestConfig, showMessage]);

  const fetchFilterOptions = useCallback(async () => {
    if (!canAccess) return;
    try {
      const response = await axios.get(`${API_URL}/companies/filter-options`, requestConfig);
      setFilterOptions((current) => ({
        ...current,
        ...(response.data?.filter_options || {}),
      }));
    } catch (error) {
      showMessage(error.response?.data?.message || "Failed to load company filters", "error");
    }
  }, [API_URL, canAccess, requestConfig, showMessage]);

  useEffect(() => {
    if (!isAuthReady || !canAccess) return undefined;
    fetchFilterOptions();
    return undefined;
  }, [canAccess, fetchFilterOptions, isAuthReady]);

  useEffect(() => {
    if (!isAuthReady || !canAccess) return undefined;
    const timer = setTimeout(() => fetchCompanies(1), 300);
    return () => clearTimeout(timer);
  }, [canAccess, fetchCompanies, isAuthReady]);

  const setFormValue = useCallback((field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  }, []);

  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuCompany(null);
  };

  const openCompanyDialog = async (mode, company = null) => {
    if (mode === "add") {
      setDialogMode("add");
      setSelectedCompanyId(null);
      setForm({ ...emptyForm });
      setDialogOpen(true);
      return;
    }

    if (!company?.id) return;
    closeMenu();
    try {
      setDialogMode(mode);
      setDialogLoading(true);
      setDialogOpen(true);
      const response = await axios.get(`${API_URL}/companies/${company.id}`, requestConfig);
      const detail = response.data?.company || {};
      setSelectedCompanyId(company.id);
      setForm({
        ...emptyForm,
        ...detail,
        additional_company_fields: normalizeAdditionalFields(detail.additional_company_fields),
        additional_bank_fields: normalizeAdditionalFields(detail.additional_bank_fields),
        company_logo: null,
        existing_company_logo: detail.company_logo || "",
        signature_image: null,
        existing_signature_image: detail.signature_image || "",
      });
    } catch (error) {
      setDialogOpen(false);
      showMessage(error.response?.data?.message || "Failed to load company details", "error");
    } finally {
      setDialogLoading(false);
    }
  };

  const handleSave = async () => {
    if (dialogMode === "add" && !canCreate) return;
    if (dialogMode === "edit" && !canEdit) return;
    if (canField("country") && !form.country) {
      showMessage("Country is required", "error");
      return;
    }
    if (form.country === "IN" && canField("state") && !form.state) {
      showMessage("State is required for IN company", "error");
      return;
    }
    if (canField("company_name") && !String(form.company_name || "").trim()) {
      showMessage("Company Name is required", "error");
      return;
    }

    try {
      setSaving(true);
      const payload = new FormData();
      editableFields.forEach((field) => {
        if (!canField(field)) return;
        const value = form[field];
        if (["signature_image", "company_logo"].includes(field)) {
          if (value instanceof File) payload.append(field, value);
        } else if (["additional_company_fields", "additional_bank_fields"].includes(field)) {
          const additionalFields = normalizeAdditionalFields(value).filter((item) => (
            item.name.trim() !== "" && item.value.trim() !== ""
          ));
          payload.append(field, JSON.stringify(additionalFields));
        } else if (["enable_tax", "enable_signature", "enable_invoice_description"].includes(field)) {
          payload.append(field, value ? "1" : "0");
        } else {
          payload.append(field, value ?? "");
        }
      });

      let response;
      if (dialogMode === "edit") {
        payload.append("_method", "PUT");
        response = await axios.post(`${API_URL}/companies/${selectedCompanyId}`, payload, requestConfig);
      } else {
        response = await axios.post(`${API_URL}/companies`, payload, requestConfig);
      }

      showMessage(response.data?.message || "Company saved successfully");
      setDialogOpen(false);
      await Promise.all([fetchCompanies(pagination.currentPage), fetchFilterOptions()]);
    } catch (error) {
      const validationErrors = error.response?.data?.errors;
      const firstValidationError = validationErrors
        ? Object.values(validationErrors).flat()[0]
        : null;
      showMessage(firstValidationError || error.response?.data?.message || "Failed to save company", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleBulkStatus = async () => {
    if (!canBulkStatus) return;
    if (selectedRows.length === 0) {
      showMessage("Please select at least one company", "error");
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(
        `${API_URL}/companies/bulk-status`,
        { company_ids: selectedRows, status: bulkStatus },
        requestConfig
      );
      showMessage(response.data?.message || "Company status updated");
      await fetchCompanies(pagination.currentPage);
    } catch (error) {
      showMessage(error.response?.data?.message || "Failed to change company status", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    if (!canExport) return;
    try {
      setIsExporting(true);
      const params = new URLSearchParams();
      appendFilters(params);
      const response = await axios.get(`${API_URL}/companies/export?${params.toString()}`, {
        ...requestConfig,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "companies.csv");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      showMessage(error.response?.data?.message || "Failed to export companies", "error");
    } finally {
      setIsExporting(false);
    }
  };

  const toggleAllRows = (event) => {
    setSelectedRows(event.target.checked ? companies.map((company) => company.id).filter(Boolean) : []);
  };

  const toggleRow = (id) => {
    setSelectedRows((current) =>
      current.includes(id) ? current.filter((companyId) => companyId !== id) : [...current, id]
    );
  };

  const visibleColumnCount = [
    canBulkStatus,
    canField("company_name"),
    canField("country"),
    canField("state"),
    canField("phone"),
    canField("email"),
    canField("status"),
    canField("created_at"),
    showActions,
  ].filter(Boolean).length || 1;

  const dialogReadOnly = dialogMode === "view";
  const dialogTitle = dialogMode === "add" ? "Add Company" : dialogMode === "edit" ? "Edit Company" : "View Company";

  if (!isAuthReady) {
    return <FlagLoader />;
  }

  if (!canAccess) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <SoftBox py={3}>
          <Card><SoftBox p={3}><SoftTypography>You do not have permission to view Company.</SoftTypography></SoftBox></Card>
        </SoftBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={2}>
        <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <SoftTypography variant="h5" fontWeight="bold">Company</SoftTypography>
          <Stack direction="row" spacing={1}>
            {canExport && (
              <SoftButton color="info" variant="outlined" size="small" onClick={handleExport} disabled={isExporting}>
                <DownloadIcon sx={{ mr: 0.5 }} /> {isExporting ? "Exporting..." : "Export"}
              </SoftButton>
            )}
            {canCreate && (
              <SoftButton color="info" size="small" onClick={() => openCompanyDialog("add")}>
                <AddIcon sx={{ mr: 0.5 }} /> Add Company
              </SoftButton>
            )}
          </Stack>
        </SoftBox>

        <Card>
          <SoftBox p={1.25}>
            <Grid container spacing={1} alignItems="stretch">
              {canFilter("search") && (
                <Grid item xs={12} sm={6} lg={2.4}>
                  <SoftBox data-full-size-search="true" sx={companySearchControlSx}>
                    <SoftInput
                      data-full-size-search="true"
                      placeholder="Search..."
                      value={filters.search}
                      onChange={(event) => setFilters((current) => ({ ...current, search: event.target.value }))}
                    />
                  </SoftBox>
                </Grid>
              )}
              {canFilter("start_date") && (
                <Grid item xs={12} sm={6} lg={2.4}>
                  <SoftBox sx={listFilterControlSx}>
                    <SoftInput
                      type="date"
                      value={filters.start_date}
                      onChange={(event) => setFilters((current) => ({ ...current, start_date: event.target.value }))}
                    />
                  </SoftBox>
                </Grid>
              )}
              {canFilter("end_date") && (
                <Grid item xs={12} sm={6} lg={2.4}>
                  <SoftBox sx={listFilterControlSx}>
                    <SoftInput
                      type="date"
                      value={filters.end_date}
                      onChange={(event) => setFilters((current) => ({ ...current, end_date: event.target.value }))}
                    />
                  </SoftBox>
                </Grid>
              )}
              {canFilter("company") && (
                <Grid item xs={12} sm={6} lg={2.4}>
                  <SoftSelect
                    sx={listFilterControlSx}
                    placeholder="Company"
                    isMulti
                    isSearchable
                    options={filterOptions.companies || []}
                    value={(filterOptions.companies || []).filter((option) =>
                      filters.company_ids.includes(String(option.value))
                    )}
                    onChange={(options) => setFilters((current) => ({
                      ...current,
                      company_ids: (options || []).map((option) => String(option.value)),
                    }))}
                  />
                </Grid>
              )}
              {canFilter("status") && (
                <Grid item xs={12} sm={6} lg={2.4}>
                  <SoftSelect
                    sx={listFilterControlSx}
                    placeholder="Status"
                    isMulti
                    isSearchable
                    options={filterOptions.statuses || statusOptions}
                    value={(filterOptions.statuses || statusOptions).filter((option) =>
                      filters.statuses.includes(option.value)
                    )}
                    onChange={(options) => setFilters((current) => ({
                      ...current,
                      statuses: (options || []).map((option) => option.value),
                    }))}
                  />
                </Grid>
              )}
            </Grid>
          </SoftBox>

          <Divider sx={{ my: 0 }} />

          <SoftBox px={1.25} py={0.75} display="flex" justifyContent="space-between" alignItems="center">
            <SoftTypography variant="button" color="text">
              Showing {pagination.from || 0} to {pagination.to || 0} of {pagination.total || 0} entries
            </SoftTypography>
            <Stack direction="row" spacing={1}>
              <SoftButton
                variant="outlined"
                color="secondary"
                size="small"
                disabled={pagination.currentPage <= 1 || loading}
                onClick={() => fetchCompanies(pagination.currentPage - 1)}
              >
                Previous
              </SoftButton>
              <SoftButton
                variant="outlined"
                color="secondary"
                size="small"
                disabled={pagination.currentPage >= pagination.lastPage || loading}
                onClick={() => fetchCompanies(pagination.currentPage + 1)}
              >
                Next
              </SoftButton>
            </Stack>
          </SoftBox>

          {canBulkStatus && (
            <SoftBox px={1.25} pb={1} display="flex" gap={1} alignItems="center">
              <SoftBox width="220px">
                <SoftSelect value={{ value: "change_status", label: "Change Status" }} isDisabled />
              </SoftBox>
              <SoftBox width="180px">
                <SoftSelect
                  options={statusOptions}
                  value={statusOptions.find((option) => option.value === bulkStatus)}
                  onChange={(option) => setBulkStatus(option?.value || "Active")}
                />
              </SoftBox>
              <SoftButton color="info" size="small" onClick={handleBulkStatus}>Apply</SoftButton>
            </SoftBox>
          )}

          <TableContainer sx={{ width: "100%", overflowX: "auto" }}>
            <Table size="small" sx={{ width: "100%", minWidth: 1100 }}>
              <TableHead sx={{ display: "table-header-group" }}>
                <TableRow>
                  {canBulkStatus && (
                    <TableCell sx={tableHeaderSx} padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={companies.length > 0 && selectedRows.length === companies.length}
                        indeterminate={selectedRows.length > 0 && selectedRows.length < companies.length}
                        onChange={toggleAllRows}
                      />
                    </TableCell>
                  )}
                  {canField("company_name") && <TableCell sx={tableHeaderSx}>Company Name</TableCell>}
                  {canField("country") && <TableCell sx={tableHeaderSx}>Country</TableCell>}
                  {canField("state") && <TableCell sx={tableHeaderSx}>State</TableCell>}
                  {canField("phone") && <TableCell sx={tableHeaderSx}>Phone</TableCell>}
                  {canField("email") && <TableCell sx={tableHeaderSx}>Email</TableCell>}
                  {canField("status") && <TableCell sx={tableHeaderSx}>Status</TableCell>}
                  {canField("created_at") && <TableCell sx={tableHeaderSx}>Created</TableCell>}
                  {showActions && <TableCell sx={tableHeaderSx} align="right">Action</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={visibleColumnCount} align="center" sx={tableBodySx}>Loading...</TableCell></TableRow>
                ) : companies.length === 0 ? (
                  <TableRow><TableCell colSpan={visibleColumnCount} align="center" sx={tableBodySx}>No companies found</TableCell></TableRow>
                ) : companies.map((company, index) => (
                  <TableRow key={company.id || `${company.company_id || "company"}-${index}`} hover>
                    {canBulkStatus && (
                      <TableCell sx={tableBodySx} padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={selectedRows.includes(company.id)}
                          onChange={() => toggleRow(company.id)}
                        />
                      </TableCell>
                    )}
                    {canField("company_name") && (
                      <TableCell sx={tableBodySx}>
                        {canView ? (
                          <SoftTypography
                            component="button"
                            variant="button"
                            color="info"
                            fontWeight="medium"
                            onClick={() => openCompanyDialog("view", company)}
                            sx={{ border: 0, background: "transparent", cursor: "pointer", p: 0 }}
                          >
                            {company.company_name || "--"}
                          </SoftTypography>
                        ) : company.company_name || "--"}
                      </TableCell>
                    )}
                    {canField("country") && <TableCell sx={tableBodySx}>{company.country || "--"}</TableCell>}
                    {canField("state") && <TableCell sx={tableBodySx}>{company.state || "--"}</TableCell>}
                    {canField("phone") && <TableCell sx={tableBodySx}>{company.phone || "--"}</TableCell>}
                    {canField("email") && <TableCell sx={tableBodySx}>{company.email || "--"}</TableCell>}
                    {canField("status") && (
                      <TableCell sx={tableBodySx}>
                        <Chip
                          size="small"
                          label={company.status || "--"}
                          sx={{
                            color: company.status === "Active" ? "#1f8a4c" : "#b45309",
                            backgroundColor: company.status === "Active" ? "#d1fae5" : "#fef3c7",
                          }}
                        />
                      </TableCell>
                    )}
                    {canField("created_at") && <TableCell sx={tableBodySx}>{formatDate(company.created_at)}</TableCell>}
                    {showActions && (
                      <TableCell sx={tableBodySx} align="right">
                        <IconButton
                          size="small"
                          onClick={(event) => {
                            setMenuAnchor(event.currentTarget);
                            setMenuCompany(company);
                          }}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </SoftBox>

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
        {canView && (
          <MenuItem onClick={() => openCompanyDialog("view", menuCompany)}>
            <VisibilityIcon fontSize="small" sx={{ mr: 1 }} /> View
          </MenuItem>
        )}
        {canEdit && (
          <MenuItem onClick={() => openCompanyDialog("edit", menuCompany)}>
            <EditIcon fontSize="small" sx={{ mr: 1 }} /> Edit
          </MenuItem>
        )}
      </Menu>

      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} fullWidth maxWidth="lg">
        <DialogTitle>{dialogTitle}</DialogTitle>
        <DialogContent dividers>
          {dialogLoading ? (
            <SoftBox py={5} textAlign="center"><SoftTypography>Loading company details...</SoftTypography></SoftBox>
          ) : (
            <>
              <FormSection title="Company Details" showFieldHeader>
                {canField("country") && (
                  <Grid container spacing={1} alignItems="center" sx={{ py: 0.75, borderTop: "1px solid #edf2f7" }}>
                    <Grid item xs={12} md={4}>
                      <SoftTypography variant="button" fontWeight="medium" color="text">
                        Country (Our Country of Invoicing) *
                      </SoftTypography>
                    </Grid>
                    <Grid item xs={12} md={8}>
                      <SoftSelect
                        placeholder="Select Country"
                        options={companyCountryOptions}
                        value={companyCountryOptions.find((option) => option.value === form.country) || null}
                        onChange={(option) => setForm((current) => ({
                          ...current,
                          country: option?.value || "",
                          state: option?.value === "IN" ? current.state : "",
                        }))}
                        isDisabled={dialogReadOnly}
                        isSearchable
                      />
                    </Grid>
                  </Grid>
                )}
                {form.country === "IN" && canField("state") && (
                  <Grid container spacing={1} alignItems="center" sx={{ py: 0.75, borderTop: "1px solid #edf2f7" }}>
                    <Grid item xs={12} md={4}>
                      <SoftTypography variant="button" fontWeight="medium" color="text">State *</SoftTypography>
                    </Grid>
                    <Grid item xs={12} md={8}>
                      <SoftSelect
                        placeholder="Select State"
                        options={clientStates.map((state) => ({ value: state.name, label: state.name }))}
                        value={form.state ? { value: form.state, label: form.state } : null}
                        onChange={(option) => setFormValue("state", option?.value || "")}
                        isDisabled={dialogReadOnly || statesLoading}
                        isLoading={statesLoading}
                        isSearchable
                      />
                    </Grid>
                  </Grid>
                )}
                <CompanyFieldRow
                  label="Company ID"
                  field="company_id"
                  form={form}
                  canField={canField}
                  readOnly
                  onChange={setFormValue}
                />
                <CompanyFieldRow
                  label="Company Name"
                  field="company_name"
                  form={form}
                  canField={canField}
                  readOnly={dialogReadOnly}
                  onChange={setFormValue}
                  required
                />
                {canField("company_logo") && (
                  <Grid container spacing={1} alignItems="center" sx={{ py: 0.75, borderTop: "1px solid #edf2f7" }}>
                    <Grid item xs={12} md={4}>
                      <SoftTypography variant="button" fontWeight="medium" color="text">Company Logo</SoftTypography>
                    </Grid>
                    <Grid item xs={12} md={8}>
                      <SoftBox
                        component={dialogReadOnly ? "div" : "label"}
                        sx={{
                          minHeight: "104px",
                          border: "1.5px dashed #cbd5e1",
                          borderRadius: "10px",
                          backgroundColor: "#f8fafc",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 1.5,
                          px: 2,
                          py: 1.5,
                          cursor: dialogReadOnly ? "default" : "pointer",
                        }}
                      >
                        {!dialogReadOnly && (
                          <input
                            hidden
                            type="file"
                            accept="image/png,image/jpeg,image/webp"
                            onChange={(event) => setFormValue("company_logo", event.target.files?.[0] || null)}
                          />
                        )}
                        {logoPreview ? (
                          <SoftBox component="img" src={logoPreview} alt="Company logo preview" sx={{ maxHeight: "76px", maxWidth: "220px", objectFit: "contain" }} />
                        ) : (
                          <CloudUploadOutlinedIcon color="info" />
                        )}
                        <SoftBox minWidth={0}>
                          <SoftTypography variant="button" fontWeight="medium" color="dark">
                            {form.company_logo instanceof File ? form.company_logo.name : logoPreview ? "Company logo uploaded" : "Choose company logo"}
                          </SoftTypography>
                          <SoftTypography variant="caption" color="text" display="block">PNG, JPG or WEBP · maximum 2 MB</SoftTypography>
                        </SoftBox>
                        {!dialogReadOnly && form.company_logo instanceof File && (
                          <IconButton
                            size="small"
                            title="Remove selected logo"
                            onClick={(event) => {
                              event.preventDefault();
                              event.stopPropagation();
                              setFormValue("company_logo", null);
                            }}
                            sx={{ ml: "auto", alignSelf: "flex-start" }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        )}
                        {!logoPreview && dialogReadOnly && <ImageOutlinedIcon color="disabled" />}
                      </SoftBox>
                    </Grid>
                  </Grid>
                )}
                <CompanyFieldRow label="Address" field="address" form={form} canField={canField} readOnly={dialogReadOnly} onChange={setFormValue} multiline />
                <CompanyFieldRow label="City / Location (required for e-invoice)" field="city" form={form} canField={canField} readOnly={dialogReadOnly} onChange={setFormValue} />
                <CompanyFieldRow label="Email" field="email" form={form} canField={canField} readOnly={dialogReadOnly} onChange={setFormValue} />
                <CompanyFieldRow label="Website" field="website" form={form} canField={canField} readOnly={dialogReadOnly} onChange={setFormValue} />
                <CompanyFieldRow label="Phone" field="phone" form={form} canField={canField} readOnly={dialogReadOnly} onChange={setFormValue} />
                <CompanyFieldRow label="Pincode" field="pincode" form={form} canField={canField} readOnly={dialogReadOnly} onChange={setFormValue} />
                <CompanyFieldRow label="GSTIN" field="gstin" form={form} canField={canField} readOnly={dialogReadOnly} onChange={setFormValue} />
                <CompanyFieldRow label="VAT Number" field="vat_number" form={form} canField={canField} readOnly={dialogReadOnly} onChange={setFormValue} />
                <CompanyFieldRow label="LUT Number" field="lut_number" form={form} canField={canField} readOnly={dialogReadOnly} onChange={setFormValue} />
                <CompanyFieldRow label="PAN" field="pan" form={form} canField={canField} readOnly={dialogReadOnly} onChange={setFormValue} />
                <CompanyFieldRow label="MSME Registration No" field="msme_registration_no" form={form} canField={canField} readOnly={dialogReadOnly} onChange={setFormValue} />
                <CompanyFieldRow label="HSN Code" field="hsn_code" form={form} canField={canField} readOnly={dialogReadOnly} onChange={setFormValue} />
                {canField("additional_company_fields") && (
                  <AdditionalDetailFields
                    title="Additional Company Details"
                    fields={form.additional_company_fields}
                    readOnly={dialogReadOnly}
                    canAdd={canAddCompanyField}
                    canRemove={canRemoveCompanyField}
                    onChange={(fields) => setFormValue("additional_company_fields", fields)}
                  />
                )}
              </FormSection>

              <FormSection title="Bank Details" showFieldHeader>
                <CompanyFieldRow label="Bank Name" field="bank_name" form={form} canField={canField} readOnly={dialogReadOnly} onChange={setFormValue} />
                <CompanyFieldRow label="Account Holder" field="account_holder" form={form} canField={canField} readOnly={dialogReadOnly} onChange={setFormValue} />
                <CompanyFieldRow label="Account Number" field="account_number" form={form} canField={canField} readOnly={dialogReadOnly} onChange={setFormValue} />
                <CompanyFieldRow label="IFSC Code" field="ifsc_code" form={form} canField={canField} readOnly={dialogReadOnly} onChange={setFormValue} />
                <CompanyFieldRow label="SWIFT Code" field="swift_code" form={form} canField={canField} readOnly={dialogReadOnly} onChange={setFormValue} />
                {canField("additional_bank_fields") && (
                  <AdditionalDetailFields
                    title="Additional Bank Details"
                    fields={form.additional_bank_fields}
                    readOnly={dialogReadOnly}
                    canAdd={canAddBankField}
                    canRemove={canRemoveBankField}
                    onChange={(fields) => setFormValue("additional_bank_fields", fields)}
                  />
                )}
              </FormSection>

              {(canField("enable_tax") || canField("tax_percentage") || canField("status")) && (
                <FormSection title="Tax & Status">
                  <Grid container spacing={2} sx={{ py: 1 }}>
                    {canField("enable_tax") && (
                      <Grid item xs={12} md={4}>
                        <SoftTypography variant="caption" fontWeight="bold">Enable Tax?</SoftTypography>
                        <SoftSelect
                          options={booleanOptions}
                          value={booleanOptions.find((option) => option.value === (form.enable_tax ? "1" : "0"))}
                          onChange={(option) => setFormValue("enable_tax", option?.value === "1")}
                          isDisabled={dialogReadOnly}
                        />
                      </Grid>
                    )}
                    {canField("tax_percentage") && (
                      <Grid item xs={12} md={4}>
                        <SoftTypography variant="caption" fontWeight="bold">Tax %</SoftTypography>
                        <SoftInput
                          type="number"
                          value={form.tax_percentage ?? ""}
                          placeholder="0.00"
                          inputProps={{ min: 0, max: 100, step: "0.01", inputMode: "decimal" }}
                          disabled={dialogReadOnly || !form.enable_tax}
                          onChange={(event) => {
                            const nextValue = event.target.value;
                            if (nextValue === "" || /^\d{0,3}(?:\.\d{0,2})?$/.test(nextValue)) {
                              setFormValue("tax_percentage", nextValue);
                            }
                          }}
                          onBlur={() => {
                            if (form.tax_percentage !== "") {
                              setFormValue("tax_percentage", Number(form.tax_percentage).toFixed(2));
                            }
                          }}
                        />
                      </Grid>
                    )}
                    {canField("status") && (
                      <Grid item xs={12} md={4}>
                        <SoftTypography variant="caption" fontWeight="bold">Status</SoftTypography>
                        <SoftSelect
                          options={statusOptions}
                          value={statusOptions.find((option) => option.value === form.status) || statusOptions[0]}
                          onChange={(option) => setFormValue("status", option?.value || "Active")}
                          isDisabled={dialogReadOnly}
                        />
                      </Grid>
                    )}
                  </Grid>
                </FormSection>
              )}

              {(canField("enable_signature") || canField("signature_image") || canField("invoice_series_pattern") || canField("enable_invoice_description") || canField("invoice_description")) && (
                <FormSection title="Invoice Settings">
                  <Grid container spacing={2} sx={{ py: 1 }}>
                    {canField("enable_signature") && (
                      <Grid item xs={12} md={4}>
                        <SoftTypography variant="caption" fontWeight="bold">Enable Signature?</SoftTypography>
                        <SoftSelect
                          options={booleanOptions}
                          value={booleanOptions.find((option) => option.value === (form.enable_signature ? "1" : "0"))}
                          onChange={(option) => setFormValue("enable_signature", option?.value === "1")}
                          isDisabled={dialogReadOnly}
                        />
                      </Grid>
                    )}
                    {canField("signature_image") && (
                      <Grid item xs={12} md={8}>
                        <SoftTypography variant="caption" fontWeight="bold">Upload Signature Image</SoftTypography>
                        <SoftBox
                          component={dialogReadOnly ? "div" : "label"}
                          mt={0.5}
                          sx={{
                            minHeight: "118px",
                            border: "1.5px dashed",
                            borderColor: form.enable_signature ? "#17c1e8" : "#cbd5e1",
                            borderRadius: "10px",
                            backgroundColor: form.enable_signature ? "#f0fbfe" : "#f8fafc",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 1.5,
                            px: 2,
                            py: 1.5,
                            cursor: !dialogReadOnly && form.enable_signature ? "pointer" : "default",
                            opacity: form.enable_signature || dialogReadOnly ? 1 : 0.6,
                            transition: "border-color 150ms ease, background-color 150ms ease",
                            "&:hover": !dialogReadOnly && form.enable_signature
                              ? { borderColor: "#078bb8", backgroundColor: "#e7f9fd" }
                              : {},
                          }}
                        >
                          {!dialogReadOnly && (
                            <input
                              hidden
                              type="file"
                              accept="image/png,image/jpeg,image/webp"
                              disabled={!form.enable_signature}
                              onChange={(event) => setFormValue("signature_image", event.target.files?.[0] || null)}
                            />
                          )}
                          {signaturePreview ? (
                            <SoftBox
                              component="img"
                              src={signaturePreview}
                              alt="Company signature preview"
                              sx={{ maxHeight: "78px", maxWidth: "220px", objectFit: "contain" }}
                            />
                          ) : (
                            <SoftBox
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                              sx={{ width: "46px", height: "46px", borderRadius: "50%", backgroundColor: "#dff6fc", color: "#078bb8" }}
                            >
                              <CloudUploadOutlinedIcon />
                            </SoftBox>
                          )}
                          <SoftBox minWidth={0}>
                            <SoftTypography variant="button" fontWeight="medium" color="dark">
                              {form.signature_image instanceof File ? form.signature_image.name : signaturePreview ? "Signature uploaded" : "Choose signature image"}
                            </SoftTypography>
                            <SoftTypography variant="caption" color="text" display="block">
                              PNG, JPG or WEBP · maximum 2 MB
                            </SoftTypography>
                            {!dialogReadOnly && form.enable_signature && (
                              <SoftTypography variant="caption" color="info" fontWeight="medium">
                                {signaturePreview ? "Click to replace" : "Click to browse"}
                              </SoftTypography>
                            )}
                          </SoftBox>
                          {!dialogReadOnly && form.signature_image instanceof File && (
                            <IconButton
                              size="small"
                              title="Remove selected file"
                              onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                setFormValue("signature_image", null);
                              }}
                              sx={{ ml: "auto", alignSelf: "flex-start" }}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          )}
                          {!signaturePreview && dialogReadOnly && <ImageOutlinedIcon color="disabled" />}
                        </SoftBox>
                      </Grid>
                    )}
                    {canField("invoice_series_pattern") && (
                      <Grid item xs={12}>
                        <SoftTypography variant="caption" fontWeight="bold">Invoice Series Pattern</SoftTypography>
                        <SoftInput value={form.invoice_series_pattern || ""} disabled={dialogReadOnly} onChange={(event) => setFormValue("invoice_series_pattern", event.target.value)} />
                        <SoftTypography variant="caption" color="text">
                          Format: QS/2026-08/34 · Use: {"{year}"}, {"{month}"}, {"{series}"}
                        </SoftTypography>
                      </Grid>
                    )}
                    {canField("enable_invoice_description") && (
                      <Grid item xs={12} md={4}>
                        <SoftTypography variant="caption" fontWeight="bold">Enable Invoice Description?</SoftTypography>
                        <SoftSelect
                          options={booleanOptions}
                          value={booleanOptions.find((option) => option.value === (form.enable_invoice_description ? "1" : "0"))}
                          onChange={(option) => setFormValue("enable_invoice_description", option?.value === "1")}
                          isDisabled={dialogReadOnly}
                        />
                      </Grid>
                    )}
                    {canField("invoice_description") && (
                      <Grid item xs={12} md={8}>
                        <SoftTypography variant="caption" fontWeight="bold">Invoice Description</SoftTypography>
                        <SoftInput
                          multiline
                          rows={3}
                          value={form.invoice_description || ""}
                          disabled={dialogReadOnly || !form.enable_invoice_description}
                          onChange={(event) => setFormValue("invoice_description", event.target.value)}
                        />
                      </Grid>
                    )}
                  </Grid>
                </FormSection>
              )}

              {(canField("created_at") || canField("updated_at")) && dialogMode !== "add" && (
                <SoftBox display="flex" gap={3}>
                  {canField("created_at") && <SoftTypography variant="caption">Created: {formatDate(form.created_at)}</SoftTypography>}
                  {canField("updated_at") && <SoftTypography variant="caption">Updated: {formatDate(form.updated_at)}</SoftTypography>}
                </SoftBox>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <SoftButton variant="outlined" color="secondary" onClick={() => setDialogOpen(false)} disabled={saving}>
            Close
          </SoftButton>
          {!dialogReadOnly && !dialogLoading && (
            <SoftButton color="info" onClick={handleSave} disabled={saving}>
              {saving ? "Saving..." : dialogMode === "add" ? "Add Company" : "Save Changes"}
            </SoftButton>
          )}
        </DialogActions>
      </Dialog>

      <SoftSnackbar
        color={snackbar.color}
        icon={snackbar.color === "success" ? "check" : "warning"}
        title={snackbar.color === "success" ? "Success" : "Notice"}
        content={snackbar.message}
        open={snackbar.open}
        onClose={() => setSnackbar((current) => ({ ...current, open: false }))}
        close={() => setSnackbar((current) => ({ ...current, open: false }))}
      />
    </DashboardLayout>
  );
}

export default Companies;
